import { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

export default function TwoFactor() {
  const [status,  setStatus]  = useState(false);
  const [qr,      setQr]      = useState('');
  const [secret,  setSecret]  = useState('');
  const [token,   setToken]   = useState('');
  const [step,    setStep]    = useState('idle'); // idle | setup | disable

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    const r = await api.get('/2fa/status');
    setStatus(r.data.enabled);
  }

  async function handleSetup() {
    try {
      const r = await api.post('/2fa/setup');
      setQr(r.data.qr); setSecret(r.data.secret); setStep('setup'); setToken('');
    } catch(e) { toast.error('Klaida'); }
  }

  async function handleVerify(e) {
    e.preventDefault();
    try {
      await api.post('/2fa/verify', { token });
      toast.success('✅ 2FA įjungta!');
      setStatus(true); setStep('idle'); setQr(''); setToken('');
    } catch(err) { toast.error(err.response?.data?.error || 'Neteisingas kodas'); }
  }

  async function handleDisable(e) {
    e.preventDefault();
    try {
      await api.post('/2fa/disable', { token });
      toast.success('2FA išjungta');
      setStatus(false); setStep('idle'); setToken('');
    } catch(err) { toast.error(err.response?.data?.error || 'Neteisingas kodas'); }
  }

  return (
    <div style={{ maxWidth:600 }}>
      <div className="page-header">
        <div className="page-title">🔐 Dviejų faktorių autentifikacija (2FA)</div>
        <div className="page-subtitle">Papildoma apsauga jūsų paskyrai</div>
      </div>

      {/* Status card */}
      <div className="card" style={{ marginBottom:20, display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:40 }}>{status ? '🔒' : '🔓'}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>
            {status ? '2FA įjungta' : '2FA išjungta'}
          </div>
          <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:4 }}>
            {status
              ? 'Jūsų paskyra apsaugota papildomu kodu iš autentifikatoriaus programėlės.'
              : 'Įjunkite 2FA norėdami apsaugoti paskyrą papildomu kodu.'}
          </div>
        </div>
        {!status && step === 'idle' && (
          <button className="btn btn-primary" onClick={handleSetup}>Įjungti 2FA</button>
        )}
        {status && step === 'idle' && (
          <button className="btn btn-danger" onClick={() => setStep('disable')}>Išjungti</button>
        )}
      </div>

      {/* Setup flow */}
      {step === 'setup' && (
        <div className="card">
          <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>📱 2FA nustatymas</div>
          <div style={{ display:'flex', gap:24, alignItems:'flex-start', flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:12 }}>
                1. Atsisiųskite <strong>Google Authenticator</strong> arba <strong>Authy</strong><br/>
                2. Nuskanuokite QR kodą<br/>
                3. Įveskite 6 skaitmenų kodą iš programėlės
              </div>
              {qr && <img src={qr} alt="QR kodas" style={{ width:180, height:180, borderRadius:8 }} />}
              <div style={{ marginTop:10, fontSize:11, color:'var(--text-muted)' }}>
                Rankinis įvedimas: <code style={{ background:'var(--bg)', padding:'2px 6px', borderRadius:4 }}>{secret}</code>
              </div>
            </div>
            <form onSubmit={handleVerify} style={{ flex:1, minWidth:200 }}>
              <div className="form-group">
                <label className="form-label">6 skaitmenų kodas iš programėlės</label>
                <input className="form-input" value={token} onChange={e => setToken(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="000000" maxLength={6} style={{ fontSize:24, textAlign:'center', letterSpacing:8 }} autoFocus />
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStep('idle')} style={{ flex:1 }}>Atšaukti</button>
                <button type="submit" className="btn btn-primary" disabled={token.length !== 6} style={{ flex:1 }}>✅ Patvirtinti</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disable flow */}
      {step === 'disable' && (
        <div className="card">
          <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>⚠️ Išjungti 2FA</div>
          <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>
            Įveskite dabartinį kodą iš autentifikatoriaus programėlės, kad patvirtintumėte išjungimą.
          </div>
          <form onSubmit={handleDisable}>
            <div className="form-group">
              <label className="form-label">6 skaitmenų kodas</label>
              <input className="form-input" value={token} onChange={e => setToken(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="000000" maxLength={6} style={{ fontSize:24, textAlign:'center', letterSpacing:8 }} autoFocus />
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('idle')} style={{ flex:1 }}>Atšaukti</button>
              <button type="submit" className="btn btn-danger" disabled={token.length !== 6} style={{ flex:1 }}>Išjungti 2FA</button>
            </div>
          </form>
        </div>
      )}

      {/* Info */}
      <div className="card" style={{ background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.2)' }}>
        <div style={{ fontSize:13, lineHeight:1.8, color:'var(--text-muted)' }}>
          <strong style={{ color:'var(--text)' }}>ℹ️ Apie 2FA</strong><br/>
          Dviejų faktorių autentifikacija reikalauja papildomo kodo prisijungiant. Net jei kas nors sužinos jūsų slaptažodį, be telefono neprisijungs. Rekomenduojamos programėlės: <strong>Google Authenticator</strong>, <strong>Authy</strong>, <strong>Microsoft Authenticator</strong>.
        </div>
      </div>
    </div>
  );
}
